'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Breadcrumb from '@/components/ui/Breadcrumb';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { blogService, type BlogPostsByCategory } from '@/lib/services/blogService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  author: string;
  createdAt: string;
  category: string;
  featuredImageUrl?: string;
  featuredImageAlt?: string;
  tags?: string[];
  keywords?: string;
  scheduledAt?: string;
  publishedAt?: string;
}

export default function BlogPage() {
  const [blogsByCategory, setBlogsByCategory] = useState<BlogPostsByCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [allTags, setAllTags] = useState<string[]>([]);
  
  // Search filter states
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await blogService.getPublishedGroupedByCategory();
        setBlogsByCategory(data);
        
        const tags = await blogService.getAllTags();
        setAllTags(tags);
      } catch (error) {
        console.error('Failed to load blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  // Get all posts for "All" category view
  const allPosts = blogsByCategory.flatMap(group => group.posts);
  
  // Get filtered posts based on all filters
  let displayedPosts = selectedCategory === 'All' 
    ? allPosts 
    : blogsByCategory.find(group => group.category === selectedCategory)?.posts || [];

  // Apply search keyword filter
  if (searchKeyword.trim()) {
    const keyword = searchKeyword.toLowerCase();
    displayedPosts = displayedPosts.filter(post => 
      post.title.toLowerCase().includes(keyword) ||
      post.excerpt.toLowerCase().includes(keyword) ||
      post.keywords?.toLowerCase().includes(keyword)
    );
  }

  // Apply tags filter (multi-select)
  if (selectedTags.length > 0) {
    displayedPosts = displayedPosts.filter(post => 
      selectedTags.some(tag => post.tags?.includes(tag))
    );
  }

  // Apply date range filter
  if (startDate) {
    displayedPosts = displayedPosts.filter(post => 
      new Date(post.createdAt) >= new Date(startDate)
    );
  }
  if (endDate) {
    displayedPosts = displayedPosts.filter(post => 
      new Date(post.createdAt) <= new Date(endDate)
    );
  }

  // Further filter by tag if selected (legacy filter)
  if (selectedTag) {
    displayedPosts = displayedPosts.filter(post => post.tags?.includes(selectedTag));
  }

  // Get all unique categories
  const categories = blogsByCategory.map(group => group.category);

  // Clear all filters
  const clearFilters = () => {
    setSearchKeyword('');
    setSelectedTags([]);
    setStartDate('');
    setEndDate('');
    setSelectedCategory('All');
    setSelectedTag('');
  };

  // Toggle tag selection
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Check if any filters are active
  const hasActiveFilters = searchKeyword || selectedTags.length > 0 || startDate || endDate || selectedCategory !== 'All' || selectedTag;

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Blog', url: `${SITE_URL}/blog` },
  ]);

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'GoGedit Blog',
    description: 'Insights, tips, and stories from the world of urban logistics',
    url: `${SITE_URL}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'GoGedit',
    },
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
      />
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'Blog' }]} />
            </div>

            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-navy-dark mb-6">GoGedit Blog</h1>
              <p className="text-xl text-body-text">
                Insights, tips, and stories from the world of urban logistics
              </p>
            </div>

            {/* Search Bar with Filters */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Keyword Search */}
                <div>
                  <label className="block text-sm font-medium text-navy-dark mb-2">
                    Search Keywords
                  </label>
                  <input
                    type="text"
                    placeholder="Search by title, excerpt, keywords..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  />
                </div>

                {/* Category Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-navy-dark mb-2">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  >
                    <option value="All">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-navy-dark mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-navy-dark mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                  />
                </div>
              </div>

              {/* Tags Multi-Select */}
              {allTags.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-navy-dark mb-2">
                    Filter by Tags (select multiple)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-brand-purple text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Result Count and Clear Filters */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-navy-dark">{displayedPosts.length}</span> {displayedPosts.length === 1 ? 'result' : 'results'} found
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm font-medium text-brand-purple hover:text-purple-700 hover:bg-purple-light rounded-lg transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            </div>

            {/* Category Tabs */}
            <div className="mb-8 border-b border-gray-200">
              <div className="flex flex-wrap gap-2 -mb-px">
                <button
                  onClick={() => setSelectedCategory('All')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                    selectedCategory === 'All' ?'border-brand-purple text-brand-purple' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  All Posts ({allPosts.length})
                </button>
                {categories.map((category) => {
                  const categoryGroup = blogsByCategory.find(g => g.category === category);
                  const count = categoryGroup?.posts.length || 0;
                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                        selectedCategory === category
                          ? 'border-brand-purple text-brand-purple' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {category} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tag Filter */}
            {allTags.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-navy-dark mb-3">Filter by Tag:</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedTag('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedTag === '' ?'bg-brand-purple text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(tag)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedTag === tag ?'bg-brand-purple text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-body-text">Loading blog posts...</p>
                </div>
              </div>
            ) : displayedPosts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No blog posts available in this category.
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="space-y-8">
                    {displayedPosts.map((post) => (
                      <div
                        key={post.id}
                        className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <div className="p-8">
                          <div className="flex items-center gap-4 mb-4">
                            <span className="text-sm font-medium text-brand-purple bg-purple-light px-3 py-1 rounded-full">
                              {post.category}
                            </span>
                          </div>
                          <Link href={`/blog/${post.slug}`} className="group">
                            <h2 className="text-2xl font-bold text-navy-dark mb-3 hover:text-brand-purple transition-colors cursor-pointer">
                              {post.title}
                            </h2>
                          </Link>
                          <p className="text-body-text mb-4">{post.excerpt}</p>
                          
                          {/* Display Tags */}
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {post.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-brand-purple rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {post.author.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-navy-dark">{post.author}</p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(post.createdAt)}
                                </p>
                              </div>
                            </div>
                            <Link href={`/blog/${post.slug}`} className="text-brand-purple font-medium hover:underline">
                              Read More →
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="sticky top-24 space-y-8">
                    <div className="bg-purple-light rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-navy-dark mb-4">Categories</h3>
                      <div className="space-y-2">
                        <button
                          onClick={() => setSelectedCategory('All')}
                          className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                            selectedCategory === 'All' ?'bg-brand-purple text-white' :'text-body-text hover:bg-white hover:text-brand-purple'
                          }`}
                        >
                          All Posts ({allPosts.length})
                        </button>
                        {categories.map((category) => {
                          const categoryGroup = blogsByCategory.find(g => g.category === category);
                          const count = categoryGroup?.posts.length || 0;
                          return (
                            <button
                              key={category}
                              onClick={() => setSelectedCategory(category)}
                              className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                                selectedCategory === category
                                  ? 'bg-brand-purple text-white' :'text-body-text hover:bg-white hover:text-brand-purple'
                              }`}
                            >
                              {category} ({count})
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Popular Tags Sidebar */}
                    {allTags.length > 0 && (
                      <div className="bg-purple-light rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-navy-dark mb-4">Popular Tags</h3>
                        <div className="flex flex-wrap gap-2">
                          {allTags.slice(0, 10).map((tag) => (
                            <button
                              key={tag}
                              onClick={() => setSelectedTag(tag)}
                              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                selectedTag === tag ?'bg-brand-purple text-white' :'bg-white text-gray-700 hover:bg-brand-purple hover:text-white'
                              }`}
                            >
                              #{tag}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="bg-gradient-to-br from-brand-purple to-brand-green rounded-2xl p-6 text-white">
                      <h3 className="text-xl font-bold mb-3">Subscribe to Our Newsletter</h3>
                      <p className="text-sm mb-4 opacity-90">
                        Get the latest blog posts and updates delivered to your inbox
                      </p>
                      <Link
                        href="/home-page#newsletter"
                        className="block w-full bg-white text-brand-purple px-4 py-3 rounded-full text-center font-medium hover:bg-opacity-90 transition-all"
                      >
                        Subscribe Now
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}